import { FunctionComponent, memo, useState, useEffect } from "react";
import { IProps } from "./metrics-distributions-form.types";
import { InteractionMetric } from "@edgetier/watchtower-types";
import { MAXIMUM_LABEL, MINIMUM_LABEL } from "@edgetier/watchtower-types";
import "./metrics-distributions-form.scss";

/**
 * Display inputs for minimum and maximum values
 * @param props.disable         Disable boolean for inputs
 * @param props.inputRange      Range selected
 * @param props.metricType      Selected metric
 * @param props.onChange        Callback for any input change
 */
const MetricsDistributionsForm: FunctionComponent<IProps> = ({ metricType, inputRange, onChange, disabled }) => {
    const [defaultMinimum, defaultMaximum] = inputRange;
    const [rangeValue, setRangeValue] = useState<[number, number]>(inputRange);

    /**
     * Handle input change event
     * @param event     Event triggered on change
     */
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldValue = Number(event.target.value);
        const fieldType = event.target.dataset && event.target.dataset.type;

        if (isNaN(fieldValue)) return;
        let returnValue = rangeValue.slice();

        // Select type
        if (fieldType === "min") returnValue = [fieldValue, rangeValue[1]];
        if (fieldType === "max") returnValue = [rangeValue[0], fieldValue];

        setRangeValue([returnValue[0], returnValue[1]]);
    };

    /**
     * Handle input blur event
     * @param event     Event triggered on blur
     */
    const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldValue = Number(event.target.value);
        const fieldType = event.target.dataset && event.target.dataset.type;
        let returnValue = rangeValue;

        if (isNaN(fieldValue)) return;

        // Prevent cases where maximum value is lower than minimum value and vice versa
        if (fieldType === "min" && fieldValue >= rangeValue[1]) returnValue = [defaultMinimum, rangeValue[1]];
        if (fieldType === "max" && fieldValue <= rangeValue[0]) returnValue = [rangeValue[0], defaultMaximum];

        setRangeValue(returnValue); // set range value
        onChange(returnValue); // callback data
    };

    useEffect(() => {
        setRangeValue(inputRange);
    }, [inputRange]);

    // Metric units
    const isTimeInteractionMetric = [
        InteractionMetric.AverageResponseTime,
        InteractionMetric.FirstResponseTime,
        InteractionMetric.HandlingTime,
        InteractionMetric.QueueTime,
    ].includes(metricType);

    return (
        <div className="interaction-metrics-distribution__form">
            <div className="field interaction-metrics-distribution__form__field">
                <label htmlFor="minimum">Minimum {isTimeInteractionMetric && <span>(seconds)</span>}</label>
                <input
                    className="field__input field__input__minimum"
                    id="minimum"
                    disabled={disabled}
                    name={MINIMUM_LABEL}
                    type="text"
                    placeholder="Minimum"
                    data-type="min"
                    value={rangeValue[0]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            </div>
            <div className="interaction-metrics-distribution__form__separator"></div>
            <div className="field interaction-metrics-distribution__form__field">
                <label htmlFor="maximum">Maximum {isTimeInteractionMetric && <span>(seconds)</span>}</label>
                <input
                    className="field__input field__input__maximum"
                    id="maximum"
                    name={MAXIMUM_LABEL}
                    disabled={disabled}
                    type="text"
                    placeholder="Maximum"
                    data-type="max"
                    value={rangeValue[1]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            </div>
        </div>
    );
};

export default memo(MetricsDistributionsForm);
